import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Grid,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete,
  Tooltip,
  Stack,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Avatar,
  Switch,
  FormControlLabel,
} from "@mui/material"
import HomeIcon from "@mui/icons-material/Home"
import ReceiptIcon from "@mui/icons-material/Receipt"
import BusinessIcon from "@mui/icons-material/Business"
import SaveIcon from "@mui/icons-material/Save"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import InventoryIcon from "@mui/icons-material/Inventory"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import PublicIcon from "@mui/icons-material/Public"

import {
  postInvoice,
  getProviders,
  getCodes,
  getMaterials,
  getInvoicesbyproid,
  patchMaterial,
  totalCost,
} from "../../helpers/api_helper"

// EU country codes (VAT prefixes) - excluding Greece (EL)
const EU_VAT_PREFIXES = [
  "AT", // Austria
  "BE", // Belgium
  "BG", // Bulgaria
  "HR", // Croatia
  "CY", // Cyprus
  "CZ", // Czech Republic
  "DK", // Denmark
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "HU", // Hungary
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LT", // Lithuania
  "LU", // Luxembourg
  "MT", // Malta
  "NL", // Netherlands
  "PL", // Poland
  "PT", // Portugal
  "RO", // Romania
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain
  "SE", // Sweden
]

// Check if VAT number is from EU (non-Greek)
const isEuVatNumber = (vatNumber) => {
  if (!vatNumber || typeof vatNumber !== "string") return false
  const cleanVat = vatNumber.trim().toUpperCase()
  // Check if starts with any EU country code (not EL for Greece)
  return EU_VAT_PREFIXES.some((prefix) => cleanVat.startsWith(prefix))
}

// Refcodes that show VAT
const VAT_CODES = new Set(["71.00.01", "24.00.01", "64.00.01", "80.00.01"])

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return "€0.00"
  return new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" }).format(amount)
}

// Parse number helper
const parseNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0
  const n = parseFloat(String(v).replace(",", "."))
  return Number.isFinite(n) ? n : 0
}

const MuiInvoiceCreate = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  document.title = `${t("Create Invoice")} | FlexFlow ERP`

  // Form state
  const [invoiceNumber, setInvoiceNumber] = useState("")
  const [importDate, setImportDate] = useState(new Date().toISOString().split("T")[0])
  const [refCode, setRefCode] = useState("")
  const [selectedProvider, setSelectedProvider] = useState(null)

  // Items state
  const [items, setItems] = useState([])

  // Material drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [materialSearch, setMaterialSearch] = useState("")
  const [materialTypeFilter, setMaterialTypeFilter] = useState("ALL")

  // Data state
  const [providers, setProviders] = useState([])
  const [codes, setCodes] = useState([])
  const [materials, setMaterials] = useState([])
  const [loadingData, setLoadingData] = useState(true)

  // UI state
  const [loading, setLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })

  // Intra-community state (for EU provider invoices with reverse charge)
  const [isIntraCommunity, setIsIntraCommunity] = useState(false)

  // Check if provider qualifies for intra-community transaction (EU VAT, non-Greek)
  const providerHasEuVat = useMemo(() => {
    return selectedProvider && isEuVatNumber(selectedProvider.vat)
  }, [selectedProvider])

  // Show VAT based on refcode (but disable if intra-community is enabled)
  const showVat = useMemo(() => {
    if (isIntraCommunity) return false
    return VAT_CODES.has(refCode)
  }, [refCode, isIntraCommunity])

  // Load data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [providersRes, codesRes, materialsRes] = await Promise.all([
          getProviders(),
          getCodes(),
          getMaterials(),
        ])
        setProviders(providersRes || [])
        setCodes(codesRes || [])
        setMaterials(materialsRes || [])
      } catch (err) {
        console.error("Error fetching data:", err)
      } finally {
        setLoadingData(false)
      }
    }
    fetchData()
  }, [])

  // Reset VAT when showVat changes
  useEffect(() => {
    if (!showVat) {
      setItems((prev) => prev.map((item) => ({ ...item, vatProducts: 0 })))
    }
  }, [showVat])

  // Reset intra-community when provider changes
  useEffect(() => {
    setIsIntraCommunity(false)
  }, [selectedProvider])

  // Material type options
  const materialTypes = [
    { value: "ALL", label: "All", color: "#6b7280" },
    { value: "MATERIAL", label: "Material", color: "#3b82f6" },
    { value: "PURCHASED-MATERIAL", label: "Purchased", color: "#8b5cf6" },
    { value: "COMPLETE-PRODUCT", label: "Product", color: "#10b981" },
  ]

  // Filtered materials for drawer
  const filteredMaterials = useMemo(() => {
    let filtered = materials

    // Filter by type
    if (materialTypeFilter !== "ALL") {
      filtered = filtered.filter((m) => m.usageType === materialTypeFilter)
    }

    // Filter by search
    if (materialSearch) {
      const searchLower = materialSearch.toLowerCase()
      filtered = filtered.filter(
        (m) =>
          m.name?.toLowerCase().includes(searchLower) ||
          m.sku?.toLowerCase().includes(searchLower) ||
          m.provsku?.toLowerCase().includes(searchLower)
      )
    }

    return filtered
  }, [materials, materialSearch, materialTypeFilter])

  // Count materials by type
  const materialCounts = useMemo(() => {
    const counts = { ALL: materials.length }
    materials.forEach((m) => {
      const type = m.usageType || "MATERIAL"
      counts[type] = (counts[type] || 0) + 1
    })
    return counts
  }, [materials])

  // Calculate totals
  const totals = useMemo(() => {
    const normalizedItems = showVat ? items : items.map((item) => ({ ...item, vatProducts: 0 }))
    const totalQuantity = normalizedItems.reduce((sum, item) => sum + parseNum(item.quantity), 0)
    const totalPrdAmmount = normalizedItems.reduce((sum, item) => sum + parseNum(item.totalPrdAmmount), 0)
    const totalVat = showVat ? normalizedItems.reduce((sum, item) => sum + parseNum(item.vatProducts), 0) : 0
    const totalOrder = totalPrdAmmount + totalVat
    return { totalQuantity, totalPrdAmmount, totalVat, totalOrder }
  }, [items, showVat])

  // Validation
  const isFormValid = invoiceNumber.trim().length > 3 && selectedProvider && refCode && items.length > 0

  // Calculate row values
  const calcRow = (row, patch = {}) => {
    const r = { ...row, ...patch }
    const qty = parseNum(r.quantity)
    const price = parseNum(r.costpricenew)
    const vatPct = parseNum(r.vat)
    const total = qty * price
    const vatVal = showVat ? total * (vatPct / 100) : 0
    return {
      ...r,
      totalPrdAmmount: total.toFixed(2),
      vatProducts: showVat ? vatVal.toFixed(2) : 0,
    }
  }

  // Add material to items
  const handleAddMaterial = (material) => {
    const alreadyAdded = items.find((item) => item.id === material.id)
    if (alreadyAdded) {
      setSnackbar({ open: true, message: t("Material already added"), severity: "warning" })
      return
    }
    const newItem = {
      id: material.id,
      name: material.name,
      sku: material.sku || "",
      unit: material.unit || "Item",
      vat: parseFloat(material.vat) || 0,
      costprice: parseFloat(material.costprice) || 0,
      costpricenew: "",
      stock: parseFloat(material.stock) || 0,
      quantity: "",
      totalPrdAmmount: 0,
      vatProducts: 0,
    }
    setItems([...items, newItem])
    setDrawerOpen(false)
    setMaterialSearch("")
  }

  // Update item field
  const handleUpdateItem = (index, field, value) => {
    setItems((prev) => {
      const newItems = [...prev]
      if (field === "costpricenew" || field === "quantity") {
        newItems[index] = calcRow(newItems[index], { [field]: value })
      } else if (field === "vatProducts") {
        newItems[index] = { ...newItems[index], vatProducts: value }
      }
      return newItems
    })
  }

  // Remove item
  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index))
  }

  // Update stock
  const updateStock = async (item) => {
    const payload = {
      resource: [{
        id: item.id,
        stock: parseFloat(item.stock) + parseFloat(item.quantity),
        costprice: parseFloat(item.costpricenew),
      }],
    }
    try {
      await patchMaterial(payload)
    } catch (err) {
      console.error("Error updating stock:", err)
    }
  }

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!isFormValid) {
      setSnackbar({ open: true, message: t("Please fill in all required fields"), severity: "warning" })
      return
    }
    setLoading(true)
    try {
      // Check for duplicate invoice
      const existingInvoices = await getInvoicesbyproid(selectedProvider.id)
      if (existingInvoices && existingInvoices.length > 0) {
        const duplicate = existingInvoices.find((inv) => inv.number === invoiceNumber)
        if (duplicate) {
          setSnackbar({ open: true, message: t("Invoice number already exists for this provider"), severity: "error" })
          setLoading(false)
          return
        }
      }

      // Normalize items based on VAT
      const normalizedItems = showVat ? items : items.map((item) => ({ ...item, vatProducts: 0 }))

      const payload = {
        resource: [{
          providerid: selectedProvider.id,
          providername: selectedProvider.name,
          providervat: selectedProvider.vat || "",
          importDate: importDate,
          number: invoiceNumber.trim(),
          type: "EXPENSE",
          content: JSON.stringify(normalizedItems),
          refcode: refCode,
          totals: JSON.stringify(totals),
          is_intra_community: isIntraCommunity,
        }],
      }

      const res = await postInvoice(payload)

      if (res && res.error) {
        if (res.error.code === 401) {
          localStorage.removeItem("authUser")
          navigate("/login")
          return
        }
        setSnackbar({ open: true, message: res.error.message || t("Failed to create invoice"), severity: "error" })
        setLoading(false)
        return
      }

      if (res && res.resource && res.resource[0]) {
        // Update stock for each material
        for (const item of normalizedItems) {
          if (item.quantity && parseFloat(item.quantity) > 0) {
            await updateStock(item)
          }
        }
        await totalCost()
        setSnackbar({ open: true, message: t("Invoice created successfully"), severity: "success" })
        setTimeout(() => navigate("/invoices"), 1000)
      }
    } catch (err) {
      console.error("Error creating invoice:", err)
      setSnackbar({ open: true, message: t("An unexpected error occurred"), severity: "error" })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Paper elevation={0} sx={{ px: 2, py: 1, mb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: 1, border: "1px solid #e5e7eb" }}>
        <Breadcrumbs separator="›" sx={{ "& .MuiBreadcrumbs-separator": { mx: 0.5, color: "#9ca3af" } }}>
          <Link component="button" onClick={() => navigate("/")} sx={{ display: "flex", alignItems: "center", color: "#6b7280", textDecoration: "none", fontSize: "0.813rem", "&:hover": { color: "#3b82f6" } }}>
            <HomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {t("Home")}
          </Link>
          <Link component="button" onClick={() => navigate("/invoices")} sx={{ color: "#6b7280", textDecoration: "none", fontSize: "0.813rem", "&:hover": { color: "#3b82f6" } }}>
            {t("Invoices")}
          </Link>
          <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827", display: "flex", alignItems: "center" }}>
            <ReceiptIcon sx={{ fontSize: 16, mr: 0.5, color: "#3b82f6" }} />
            {t("Create Invoice")}
          </Typography>
        </Breadcrumbs>
        <Button
          size="small"
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate("/invoices")}
          sx={{ textTransform: "none", fontSize: "0.75rem", color: "#6b7280" }}
        >
          {t("Back")}
        </Button>
      </Paper>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={1.5}>
          {/* Left Column - Form */}
          <Grid item xs={12} lg={8}>
            {/* Invoice Details */}
            <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden", mb: 1.5 }}>
              <Box sx={{ px: 2, py: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827" }}>
                  {t("Invoice Details")}
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {/* Expense Code */}
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth required>
                      <InputLabel sx={{ fontSize: "0.813rem" }}>{t("Expense Code")}</InputLabel>
                      <Select
                        value={refCode}
                        label={t("Expense Code")}
                        onChange={(e) => setRefCode(e.target.value)}
                        sx={{ "& .MuiSelect-select": { py: 1, fontSize: "0.813rem" } }}
                      >
                        <MenuItem value=""><em>{t("Select code")}</em></MenuItem>
                        {codes.map((code) => (
                          <MenuItem key={code.id} value={code.code}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip label={code.code} size="small" sx={{ height: 20, fontSize: "0.65rem", bgcolor: "#fef3c7", color: "#92400e" }} />
                              <Typography sx={{ fontSize: "0.813rem" }}>{code.name}</Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {showVat && (
                      <Typography sx={{ fontSize: "0.65rem", color: "#16a34a", mt: 0.5 }}>
                        {t("VAT will be calculated for this code")}
                      </Typography>
                    )}
                  </Grid>

                  {/* Provider */}
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      size="small"
                      options={providers}
                      getOptionLabel={(option) => option.name || ""}
                      value={selectedProvider}
                      onChange={(_, newValue) => setSelectedProvider(newValue)}
                      loading={loadingData}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t("Provider")}
                          placeholder={t("Search...")}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <BusinessIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                          sx={{ "& input": { fontSize: "0.813rem" } }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography sx={{ fontSize: "0.813rem", fontWeight: 500 }}>{option.name}</Typography>
                            <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>
                              AFM: {option.vat || "-"}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>

                  {/* Invoice Number */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      required
                      label={t("Invoice Number")}
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                      placeholder={t("e.g. INV-001")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ReceiptIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& input": { fontSize: "0.813rem" } }}
                      error={invoiceNumber.length > 0 && invoiceNumber.length <= 3}
                      helperText={invoiceNumber.length > 0 && invoiceNumber.length <= 3 ? t("Min 4 characters") : ""}
                    />
                  </Grid>

                  {/* Invoice Date */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="date"
                      label={t("Invoice Date")}
                      value={importDate}
                      onChange={(e) => setImportDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& input": { fontSize: "0.813rem" } }}
                    />
                  </Grid>
                </Grid>

                {/* Provider Info */}
                {selectedProvider && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f0fdf4", borderRadius: 1, border: "1px solid #bbf7d0" }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={2} alignItems="center">
                        <BusinessIcon sx={{ color: "#16a34a" }} />
                        <Box>
                          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#166534" }}>
                            {selectedProvider.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#15803d" }}>
                            AFM: {selectedProvider.vat || "-"} • {selectedProvider.phone || "-"}
                          </Typography>
                        </Box>
                      </Stack>
                      {/* Intra-community toggle - shown only for EU providers */}
                      {providerHasEuVat && (
                        <Tooltip title={t("Apply reverse charge (0% VAT) for intra-community purchases from EU providers")}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              bgcolor: isIntraCommunity ? "#dbeafe" : "#f3f4f6",
                              borderRadius: 1,
                              border: isIntraCommunity ? "1px solid #3b82f6" : "1px solid #e5e7eb",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <PublicIcon sx={{ fontSize: 16, color: isIntraCommunity ? "#3b82f6" : "#6b7280" }} />
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={isIntraCommunity}
                                  onChange={(e) => setIsIntraCommunity(e.target.checked)}
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#3b82f6" },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#3b82f6" },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    fontWeight: isIntraCommunity ? 600 : 400,
                                    color: isIntraCommunity ? "#1d4ed8" : "#6b7280",
                                  }}
                                >
                                  {t("Intra-community")}
                                </Typography>
                              }
                              sx={{ m: 0 }}
                            />
                            {isIntraCommunity && (
                              <Chip
                                label={t("Reverse Charge")}
                                size="small"
                                sx={{ height: 18, fontSize: "0.6rem", bgcolor: "#dcfce7", color: "#166534", fontWeight: 600 }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Add Materials Button */}
            <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden", mb: 1.5 }}>
              <Box sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <InventoryIcon sx={{ fontSize: 20, color: "#6b7280" }} />
                  <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827" }}>
                    {t("Materials")}
                  </Typography>
                  <Chip label={`${items.length} ${t("added")}`} size="small" sx={{ height: 20, fontSize: "0.65rem", bgcolor: "#dbeafe", color: "#1d4ed8" }} />
                </Stack>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setDrawerOpen(true)}
                  sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.75, bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
                >
                  {t("Add Material")}
                </Button>
              </Box>
            </Paper>

            {/* Items Table */}
            <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827" }}>
                  {t("Invoice Items")}
                </Typography>
                {showVat && (
                  <Chip label={t("VAT Included")} size="small" sx={{ height: 20, fontSize: "0.65rem", bgcolor: "#dcfce7", color: "#166534" }} />
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f9fafb" }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1 }}>{t("Name")}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1 }}>{t("Unit")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1 }}>{t("Old €")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1 }}>{t("New €")}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1 }}>{t("Qty")}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1 }}>{t("Stock+")}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1 }}>{t("Total")}</TableCell>
                      {showVat && <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.7rem", color: "#6b7280", textTransform: "uppercase", py: 1 }}>{t("VAT")}</TableCell>}
                      <TableCell sx={{ py: 1 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length > 0 ? (
                      items.map((item, index) => {
                        const stockAfter = (parseFloat(item.stock) || 0) + (parseFloat(item.quantity) || 0)
                        return (
                          <TableRow key={item.id} hover sx={{ "&:hover": { bgcolor: "#f9fafb" } }}>
                            <TableCell sx={{ fontSize: "0.813rem", py: 1 }}>{index + 1}</TableCell>
                            <TableCell sx={{ fontSize: "0.813rem", fontWeight: 500, py: 1 }}>
                              <Box>
                                <Typography sx={{ fontSize: "0.813rem", fontWeight: 500 }}>{item.name}</Typography>
                                {item.sku && <Typography sx={{ fontSize: "0.65rem", color: "#6b7280" }}>SKU: {item.sku}</Typography>}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.75rem", color: "#6b7280", py: 1 }}>{item.unit}</TableCell>
                            <TableCell align="right" sx={{ fontSize: "0.75rem", color: "#6b7280", py: 1 }}>
                              {formatCurrency(item.costprice)}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={item.costpricenew}
                                onChange={(e) => handleUpdateItem(index, "costpricenew", e.target.value)}
                                inputProps={{ min: 0, step: "0.01", style: { textAlign: "right", fontSize: "0.75rem" } }}
                                sx={{ width: 80, "& .MuiOutlinedInput-root": { height: 28 } }}
                                placeholder="0.00"
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={(e) => handleUpdateItem(index, "quantity", e.target.value)}
                                inputProps={{ min: 0, step: "0.01", style: { textAlign: "center", fontSize: "0.75rem" } }}
                                sx={{ width: 70, "& .MuiOutlinedInput-root": { height: 28 } }}
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1 }}>
                              <Chip
                                label={stockAfter.toFixed(1)}
                                size="small"
                                sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600, bgcolor: stockAfter > 0 ? "#dcfce7" : "#fee2e2", color: stockAfter > 0 ? "#166534" : "#991b1b" }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: "0.813rem", fontWeight: 500, py: 1 }}>
                              {formatCurrency(parseFloat(item.totalPrdAmmount) || 0)}
                            </TableCell>
                            {showVat && (
                              <TableCell align="right" sx={{ py: 1 }}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={item.vatProducts}
                                  onChange={(e) => handleUpdateItem(index, "vatProducts", e.target.value)}
                                  inputProps={{ min: 0, step: "0.01", style: { textAlign: "right", fontSize: "0.75rem" } }}
                                  sx={{ width: 70, "& .MuiOutlinedInput-root": { height: 28 } }}
                                  placeholder="0.00"
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ py: 1 }}>
                              <Tooltip title={t("Remove")}>
                                <IconButton size="small" onClick={() => handleRemoveItem(index)} sx={{ p: 0.5, color: "#ef4444" }}>
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={showVat ? 10 : 9} align="center" sx={{ py: 4 }}>
                          <InventoryIcon sx={{ fontSize: 36, color: "#d1d5db", mb: 1 }} />
                          <Typography sx={{ fontSize: "0.813rem", color: "#9ca3af" }}>
                            {t("No materials added yet. Click 'Add Material' to get started.")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Right Column - Summary */}
          <Grid item xs={12} lg={4}>
            <Paper elevation={0} sx={{ borderRadius: 1, border: "1px solid #e5e7eb", overflow: "hidden", position: "sticky", top: 16 }}>
              <Box sx={{ px: 2, py: 1, bgcolor: "#dbeafe", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}>
                <ReceiptIcon sx={{ fontSize: 18, color: "#1d4ed8" }} />
                <Typography sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#1d4ed8" }}>
                  {t("Invoice Summary")}
                </Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {/* Selected Code */}
                {refCode && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: "#fef3c7", borderRadius: 1, border: "1px solid #fde68a", textAlign: "center" }}>
                    <Chip label={refCode} size="small" sx={{ fontWeight: 600, bgcolor: "#f59e0b", color: "#fff", mb: 0.5 }} />
                    <Typography sx={{ fontSize: "0.75rem", color: "#92400e" }}>
                      {codes.find((c) => c.code === refCode)?.name || ""}
                    </Typography>
                    {showVat && (
                      <Chip label={t("VAT Enabled")} size="small" sx={{ mt: 1, height: 18, fontSize: "0.6rem", bgcolor: "#dcfce7", color: "#166534" }} />
                    )}
                  </Box>
                )}

                {/* Totals */}
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>{t("Items")}</Typography>
                    <Typography sx={{ fontSize: "0.813rem", fontWeight: 500 }}>{items.length}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>{t("Quantity")}</Typography>
                    <Typography sx={{ fontSize: "0.813rem", fontWeight: 500 }}>{totals.totalQuantity.toFixed(2)}</Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>{t("Subtotal")}</Typography>
                    <Typography sx={{ fontSize: "0.813rem" }}>{formatCurrency(totals.totalPrdAmmount)}</Typography>
                  </Stack>
                  {showVat && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: "0.813rem", color: "#6b7280" }}>{t("VAT")}</Typography>
                      <Typography sx={{ fontSize: "0.813rem", color: "#16a34a" }}>{formatCurrency(totals.totalVat)}</Typography>
                    </Stack>
                  )}
                  <Divider sx={{ borderWidth: 2 }} />
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#111827" }}>{t("Total")}</Typography>
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#3b82f6" }}>
                      {formatCurrency(totals.totalOrder)}
                    </Typography>
                  </Stack>
                </Stack>

                {/* Action Buttons */}
                <Stack spacing={1} sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={!isFormValid || loading}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1.25,
                      bgcolor: "#3b82f6",
                      "&:hover": { bgcolor: "#2563eb" },
                    }}
                  >
                    {loading ? t("Creating...") : t("Create Invoice")}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate("/invoices")}
                    disabled={loading}
                    sx={{ textTransform: "none", fontSize: "0.813rem", py: 1, borderColor: "#e5e7eb", color: "#6b7280" }}
                  >
                    {t("Cancel")}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </form>

      {/* Material Selection Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setMaterialSearch(""); setMaterialTypeFilter("ALL") }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#111827" }}>
            {t("Select Material")}
          </Typography>
          <IconButton size="small" onClick={() => { setDrawerOpen(false); setMaterialSearch(""); setMaterialTypeFilter("ALL") }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("Search materials...")}
            value={materialSearch}
            onChange={(e) => setMaterialSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{ "& input": { fontSize: "0.875rem" }, mb: 1.5 }}
          />
          {/* Type Filter Chips */}
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {materialTypes.map((type) => (
              <Chip
                key={type.value}
                label={`${t(type.label)} (${materialCounts[type.value] || 0})`}
                size="small"
                onClick={() => setMaterialTypeFilter(type.value)}
                sx={{
                  height: 26,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  bgcolor: materialTypeFilter === type.value ? type.color : "#f3f4f6",
                  color: materialTypeFilter === type.value ? "#fff" : "#374151",
                  border: materialTypeFilter === type.value ? "none" : "1px solid #e5e7eb",
                  "&:hover": {
                    bgcolor: materialTypeFilter === type.value ? type.color : "#e5e7eb",
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
        <List sx={{ flex: 1, overflow: "auto", py: 0 }}>
          {filteredMaterials.length > 0 ? (
            filteredMaterials.map((material, index) => {
              const isAdded = items.some((item) => item.id === material.id)
              return (
                <ListItem key={material.id} disablePadding divider>
                  <ListItemButton
                    onClick={() => !isAdded && handleAddMaterial(material)}
                    disabled={isAdded}
                    sx={{
                      py: 1.5,
                      bgcolor: index % 2 === 0 ? "#f9fafb" : "#fff",
                      "&:hover": { bgcolor: isAdded ? undefined : "#e0f2fe" },
                      opacity: isAdded ? 0.5 : 1,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: isAdded ? "#d1d5db" : "#dbeafe", color: isAdded ? "#6b7280" : "#3b82f6" }}>
                        <InventoryIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#111827" }}>
                            {material.name}
                          </Typography>
                          <Chip
                            label={material.usageType === "PURCHASED-MATERIAL" ? "Purch" : material.usageType === "COMPLETE-PRODUCT" ? "Prod" : "Mat"}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              bgcolor: material.usageType === "PURCHASED-MATERIAL" ? "#f3e8ff" : material.usageType === "COMPLETE-PRODUCT" ? "#d1fae5" : "#dbeafe",
                              color: material.usageType === "PURCHASED-MATERIAL" ? "#7c3aed" : material.usageType === "COMPLETE-PRODUCT" ? "#059669" : "#2563eb",
                            }}
                          />
                        </Stack>
                      }
                      secondary={
                        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                          <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>
                            SKU: {material.sku || "-"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>
                            Stock: {material.stock || 0}
                          </Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: "#16a34a", fontWeight: 500 }}>
                            {formatCurrency(material.costprice)}
                          </Typography>
                        </Stack>
                      }
                    />
                    {isAdded && (
                      <Chip label={t("Added")} size="small" sx={{ height: 20, fontSize: "0.65rem", bgcolor: "#d1d5db", color: "#374151" }} />
                    )}
                  </ListItemButton>
                </ListItem>
              )
            })
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <InventoryIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
              <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                {materialSearch ? t("No materials found") : t("No materials available")}
              </Typography>
            </Box>
          )}
        </List>
      </Drawer>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%", borderRadius: 1 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default MuiInvoiceCreate
